import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { BulkOperationService } from 'src/app/core/providers/bulk-operation/bulk-operation.service';
import { BulkOperationState } from 'src/app/core/providers/bulk-operation/bulk-operation.models';

@Component({
  selector: 'app-bulk-progress-toast',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  template: `
    @if (state && state.operationStatus !== 'cancelled') {
      <div class="bulk-toast" [class.bulk-toast--completed]="state.operationStatus === 'completed'" [class.bulk-toast--failed]="state.operationStatus === 'failed'">
        <!-- Header -->
        <div class="bulk-toast__header">
          <div class="bulk-toast__title">
            @if (state.operationStatus === 'running') {
              <span class="spinner-border spinner-border-sm text-primary me-2" role="status"></span>
            }
            @if (state.operationStatus === 'completed') {
              <i class="bi bi-check-circle-fill text-success me-2"></i>
            }
            @if (state.operationStatus === 'failed') {
              <i class="bi bi-exclamation-triangle-fill text-danger me-2"></i>
            }
            <span class="fw-semibold small">{{ state.label }}</span>
          </div>
          <div class="bulk-toast__actions">
            @if (state.operationStatus === 'running' && isCancellable(state)) {
              <button type="button" class="btn-icon text-muted" (click)="cancel()" [title]="'bulkProgressToast.cancel' | translate">
                <i class="bi bi-x-circle"></i>
              </button>
            }
            @if (state.operationStatus === 'running' && !isCancellable(state)) {
              <button type="button" class="btn-icon text-muted" (click)="dismiss()" [title]="'bulkProgressToast.dismiss' | translate">
                <i class="bi bi-x"></i>
              </button>
            }
            @if (state.operationStatus === 'completed' || state.operationStatus === 'failed') {
              <button type="button" class="btn-icon text-muted" (click)="dismiss()" [title]="'bulkProgressToast.dismiss' | translate">
                <i class="bi bi-x"></i>
              </button>
            }
          </div>
        </div>

        <!-- Progress bar -->
        @if (state.operationStatus === 'running' || state.operationStatus === 'completed') {
          <div class="bulk-toast__progress-wrap">
            <div class="d-flex justify-content-between small text-muted mb-1">
              <span>
                @if (state.operationStatus === 'running') {
                  {{ 'bulkProgressToast.inProgress' | translate }}
                } @else {
                  {{ 'bulkProgressToast.done' | translate }}
                }
              </span>
              <span class="fw-bold">{{ state.processedCount }} / {{ getTotalCount(state) }}</span>
            </div>
            <div class="progress" style="height: 5px;">
              <div
                class="progress-bar"
                [class.bg-primary]="state.operationStatus === 'running'"
                [class.bg-success]="state.operationStatus === 'completed'"
                role="progressbar"
                [style.width.%]="getProgressPercentage(state)"
              ></div>
            </div>
          </div>
        }

        <!-- Error -->
        @if (state.operationStatus === 'failed' && state.errors.length > 0) {
          <div class="bulk-toast__error small">
            <i class="bi bi-exclamation-circle me-1"></i>
            {{ state.errors[0] }}
          </div>
          <div class="bulk-toast__error-meta small text-muted">
            {{ getFailureLabel(state) | translate : { count: state.processedCount, total: getTotalCount(state) } }}
          </div>
        }
      </div>
    }
  `,
  styleUrl: './bulk-progress-toast.component.scss',
})
export class BulkProgressToastComponent implements OnInit, OnDestroy {
  state: BulkOperationState | null = null;
  private sub!: Subscription;

  constructor(
    private bulkOperationService: BulkOperationService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.sub = this.bulkOperationService.state$.subscribe((state) => {
      this.state = state;
      this.cdr.markForCheck();
    });
  }

  cancel(): void {
    this.bulkOperationService.cancel();
  }

  dismiss(): void {
    this.bulkOperationService.dismiss();
  }

  isCancellable(state: BulkOperationState): boolean {
    return state.type !== 'impots_messages' && state.type !== 'impots_cfe';
  }

  getTotalCount(state: BulkOperationState): number {
    return state.taskIds.length;
  }

  getProgressPercentage(state: BulkOperationState): number {
    const total = this.getTotalCount(state);
    if (total === 0) {
      return 0;
    }

    return (state.processedCount / total) * 100;
  }

  getFailureLabel(state: BulkOperationState): string {
    return state.type === 'impots_messages' || state.type === 'impots_cfe'
      ? 'bulkProgressToast.failedAtSirens'
      : 'bulkProgressToast.failedAt';
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }
}
